import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ActivatedRoute } from "@angular/router";
import moment from "moment";
import { StorageService, StorageKeys } from "./storage.service";

export interface Language {
  code: string;
  name: string;
  translated_name?: string;
  flag_icon: string;
}

@Injectable({
  providedIn: "root",
})
export class LanguageService {
  public availableLanguages: Record<string, boolean> = {
    cn: true,
    de: true,
    en: true,
    es: true,
    ko: true,
    fr: true,
    it: true,
    ja: true,
    jp: true,
    tr: true,
    zh: true,
    pl: true,
  };

  public languages: Language[] = [
    {
      code: "en",
      name: "English",
      flag_icon: "./assets/images/flags/us.png",
    },
    {
      code: "es",
      name: "Español",
      translated_name: "Spanish",
      flag_icon: "./assets/images/flags/es.png",
    },
    {
      code: "ko",
      name: "Korean",
      translated_name: "한국어",
      flag_icon: "./assets/images/flags/ko.png",
    },
    {
      code: "de",
      name: "Deutsch",
      translated_name: "German",
      flag_icon: "./assets/images/flags/de.png",
    },
    {
      code: "jp",
      name: "日本語",
      translated_name: "Japanese",
      flag_icon: "./assets/images/flags/jp.png",
    },
    {
      code: "cn",
      name: "中文",
      translated_name: "Chinese",
      flag_icon: "./assets/images/flags/cn.png",
    },
    {
      code: "fr",
      name: "Français",
      translated_name: "French",
      flag_icon: "./assets/images/flags/fr.png",
    },
    {
      code: "tr",
      name: "Türk",
      translated_name: "Turkish",
      flag_icon: "./assets/images/flags/tr.png",
    },
    {
      code: "it",
      name: "Italiano",
      translated_name: "Italian",
      flag_icon: "./assets/images/flags/it.png",
    },
    {
      code: "pl",
      name: "Polski",
      translated_name: "Polish",
      flag_icon: "./assets/images/flags/pl.png",
    },
  ];

  private _selectedLanguage: Language | null = null;

  constructor(
    private _storageService: StorageService,
    private _translateService: TranslateService
  ) {}

  get selectedLanguage(): Language {
    const storedLang = this._storageService.getItem(StorageKeys.LANGUAGE);
    const myLang = this._selectedLanguage ? this._selectedLanguage : this.languages.find((item) => item.code === (storedLang || "en")) || this.languages[0];
    return myLang;
  }

  set selectedLanguage(lang: string | Language) {
    let myLang: Language;
    if (typeof lang === "string") {
      myLang = this.languages.find((item) => item.code === lang) || this.languages[0];
    } else {
      myLang = lang;
    }
    this._translateService.setDefaultLang(myLang.code);
    this._translateService.use(myLang.code);
    moment.locale(myLang.code);
    this._storageService.setItem(StorageKeys.LANGUAGE, myLang.code);
    this._selectedLanguage = myLang;
  }

  /**
   * Sets application language based on URL params, user configuration, and local storage
   * Priority order:
   * 1. URL 'lang' parameter (also saves to user if logged in)
   * 2. User's saved language_code
   * 3. Local storage saved language
   * 4. Default to 'en'
   *
   * @param activatedRoute - ActivatedRoute to get URL parameters
   * @param user - Current user object (optional)
   * @param saveUserLanguage - Callback function to save language to user (optional)
   */
  setApplicationLanguageFromParams(activatedRoute: ActivatedRoute, user?: any, saveUserLanguage?: (userId: string, languageCode: string) => void): void {
    let languageToUse = "en";

    // Priority 1: Check URL parameter
    const langParam = activatedRoute.snapshot.queryParamMap.get("lang");
    if (langParam && this.availableLanguages[langParam]) {
      languageToUse = langParam;

      // If user is logged in and callback provided, save to user profile
      if (user && saveUserLanguage) {
        saveUserLanguage(user.id, langParam);
      }
    }
    // Priority 2: Check user's saved language
    else if (user && user.language_code) {
      languageToUse = user.language_code;
    }
    // Priority 3: Check local storage
    else {
      const savedLanguage = this._storageService.getItem(StorageKeys.LANGUAGE);
      if (savedLanguage) {
        languageToUse = savedLanguage;
      }
    }

    // Validate and fallback to English if invalid
    if (!this.availableLanguages[languageToUse]) {
      languageToUse = "en";
    }

    this.selectedLanguage = languageToUse;
  }

  /**
   * Initialize application language from user and storage
   * This is a simpler version for apps that don't need URL parameter handling
   *
   * @param user - Current user object (optional)
   * @returns The language code that was set
   */
  initializeLanguage(user?: any): string {
    let languageToUse = "en";

    if (user && user.language_code) {
      languageToUse = user.language_code;
    } else {
      const savedLanguage = this._storageService.getItem(StorageKeys.LANGUAGE);
      if (savedLanguage) {
        languageToUse = savedLanguage;
      }
    }

    if (!this.availableLanguages[languageToUse]) {
      languageToUse = "en";
    }

    this.selectedLanguage = languageToUse;
    return languageToUse;
  }

  /**
   * Get language object by code
   * @param code - Language code
   * @returns Language object or undefined
   */
  getLanguageByCode(code: string): Language | undefined {
    return this.languages.find((lang) => lang.code === code);
  }

  /**
   * Check if a language code is valid/available
   * @param code - Language code to check
   * @returns true if language is available
   */
  isLanguageAvailable(code: string): boolean {
    return !!this.availableLanguages[code];
  }
}
